import Grid from '@material-ui/core/Grid';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useStore } from 'core/store/store.mobx';
import { lookupsJson } from '@ourbranch/lookups';
import { Select } from 'core/components/select';
import { LabelTooltip } from 'core/components/label-tooltip';
import { tooltipHoverTexts } from 'core/helpers/constants';

const id = 'autoCoverage';

const SplitLiabilityLimit = ({ disabled }) => {
  const { offer: store } = useStore();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  // An offer can arrive with no BI/PD limit. Hold a half-picked limit here and only write it once
  // both BI and PD are chosen, so the form never stores a partial value like "/undefined/25".
  const [biPerPerson, biPerAccident, storedPD] = (values[id].policyLimitBIPD ?? '').split('/');
  const [pendingLimit, setPendingLimit] = useState({ bi: '', pd: '' });
  const bi = biPerPerson ? `${biPerPerson}/${biPerAccident}` : pendingLimit.bi;
  const pd = storedPD || pendingLimit.pd;

  const currentOption = store?.offer?.options?.find((offer) => offer.type === values.selectedOption);
  const setLimit = useCallback(
    (nextBI, nextPD) => {
      if (!nextBI || !nextPD) {
        setPendingLimit({ bi: nextBI, pd: nextPD });
        return;
      }
      setPendingLimit({ bi: '', pd: '' });
      setFieldValue(`${id}.policyLimitBIPD`, `${nextBI}/${nextPD}`);
      setFieldTouched(`${id}.policyLimitBIPD`);
    },
    [setFieldValue, setFieldTouched]
  );
  const setBI = useCallback((_, value) => setLimit(value, pd), [pd, setLimit]);
  const setPD = useCallback((_, value) => setLimit(bi, value), [bi, setLimit]);

  const biOptions =
    store.offer &&
    currentOption?.alternateAutoOptionsByBIPD &&
    Object.values(currentOption?.alternateAutoOptionsByBIPD?.bi || {}).length > 0
      ? Object.values(currentOption?.alternateAutoOptionsByBIPD?.bi || {})
      : lookupsJson.splitPolicyLimitBIPDState.MI.bi;
  const pdOptions = lookupsJson.splitPolicyLimitBIPDState.MI.pd;

  return (
    <>
      <Grid item xs={6}>
        <LabelTooltip
          label="Bodily Injury Damage Limit"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.policyLimitBIPD }}
        >
          <Select
            fullWidth
            mode="dark"
            name={`${id}.policyLimitBI`}
            id={`${id}.policyLimitBI`}
            value={bi}
            options={biOptions}
            disabled={disabled}
            onChange={setBI}
          />
        </LabelTooltip>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label="Property Damage Limit"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.policyLimitBIPD }}
        >
          <Select
            fullWidth
            mode="dark"
            name={`${id}.policyLimitPD`}
            id={`${id}.policyLimitPD`}
            value={pd}
            options={pdOptions}
            disabled={disabled}
            onChange={setPD}
          />
        </LabelTooltip>
      </Grid>
    </>
  );
};

SplitLiabilityLimit.propTypes = {
  disabled: PropTypes.bool
};

SplitLiabilityLimit.defaultProps = {
  disabled: false
};

export default SplitLiabilityLimit;
